import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';

/**
 * Correct the LR / AWB number on a shipment that is still on the manifest.
 *
 * Renders NOTHING unless the server says this account may do it AND the order is at a status where
 * the number can still be corrected — both answers come from `/orders/merchant-reassign/allowed`,
 * so the rule lives in one place and the screen cannot drift from what the API will accept.
 *
 * Deliberately separate from Edit Order: the edit form refuses to touch a shipment booked with an
 * external courier at all, and almost every mis-keyed number is on exactly such a booking.
 */
export function ChangeWaybillButton({
  orderId,
  orderNo,
  orderType,
  statusId,
  awbNo,
  lrNo,
}: {
  orderId: string | number;
  orderNo?: string | null;
  orderType: 'B2C' | 'B2B';
  statusId: number;
  awbNo?: string | null;
  lrNo?: string | null;
}) {
  const qc = useQueryClient();
  const isB2B = orderType === 'B2B';
  const field = isB2B ? 'lrNo' : 'awbNo';
  const label = isB2B ? 'LR' : 'AWB';
  const current = (isB2B ? lrNo : awbNo) ?? '';

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  const { data: gate } = useQuery({
    queryKey: ['order-merchant-reassign-allowed'],
    queryFn: async () =>
      (await api.get<{ allowed: boolean; waybillStatuses?: number[] }>('/orders/merchant-reassign/allowed')).data,
    staleTime: 10 * 60_000,
    retry: false,
  });

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch(`/orders/${orderId}/waybill`, { [field]: value.trim(), reason: reason.trim() || undefined })).data,
    onSuccess: (r: any) => {
      toast.success(r?.message ?? `${label} updated`);
      qc.invalidateQueries({ queryKey: ['order', String(orderId)] });
      qc.invalidateQueries({ queryKey: ['order-edit-logs', String(orderId)] });
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && q.queryKey[0] === 'orders' });
      setOpen(false); setValue(''); setReason('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? `Could not change the ${label}`),
  });

  if (!gate?.allowed) return null;
  if (!(gate.waybillStatuses ?? []).includes(statusId)) return null;

  return (
    <>
      <button type="button" onClick={() => { setValue(current); setOpen(true); }}
        className="ml-2 text-[11px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 align-middle">
        Change
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={`Change ${label} number`}
        subtitle={`${orderNo ?? `Order ${orderId}`} — currently ${current || 'not set'}`} maxWidth="md">
        <div className="space-y-3">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900 leading-relaxed">
            Only while the shipment is still on the manifest. Scans already received keep the old
            number in their own rows — they are the courier's words at that time — but the timeline
            stays with this order. Recorded in the Edit History with your name and the time.
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">New {label} number *</label>
            <input value={value} onChange={(e) => setValue(e.target.value)} className="input font-mono"
              placeholder={`Corrected ${label} number`} />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason (optional — kept in the history)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} className="input"
              placeholder="e.g. number mis-keyed at booking" />
          </div>

          <div className="flex justify-end gap-2 pt-1 border-t border-slate-100">
            <button type="button" onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button type="button" onClick={() => save.mutate()}
              disabled={!value.trim() || value.trim() === current || save.isPending}
              className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 disabled:opacity-60 inline-flex items-center gap-2">
              {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Hash size={14} />}
              Save {label}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
